import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useSchema } from "../lib/schema";
import ResourceTable from "./ResourceTable";
import type { ListResponse, RelatedSchema } from "../lib/types";
import { number } from "../lib/format";

const PAGE_SIZE = 25;

const OVERVIEW = "__overview";

/**
 * Everything hanging off one record — a garage's bookings, quotes, staff — on
 * the garage's own page, so answering "what is this garage on the hook for?"
 * does not mean walking back out to five separate tables.
 *
 * Each tab is the ordinary list endpoint with `scope[key]`/`scope[id]`, so the
 * columns, formatting and drill-down links are the registry's, not a second
 * set maintained here.
 */
export default function RelatedTabs({
  parentKey, parentId, related, overview,
}: {
  parentKey: string;
  parentId: number;
  related: RelatedSchema[];
  // The record's own fields, as the first tab — otherwise a tall detail card
  // pushes every related table below the fold.
  overview?: ReactNode;
}) {
  const [active, setActive] = useState(overview ? OVERVIEW : related[0]?.key);
  const [page, setPage] = useState(1);
  const { data: schema } = useSchema();

  const activeResource = schema?.resources.find((r) => r.key === active);

  const { data, isFetching } = useQuery({
    queryKey: ["related", parentKey, parentId, active, page],
    queryFn: () =>
      api.get<ListResponse>(`/superadmin/resources/${active}`, {
        per_page: PAGE_SIZE,
        page,
        scope: { key: parentKey, id: String(parentId) },
      }),
    enabled: Boolean(active && activeResource),
    placeholderData: keepPreviousData,
  });

  if (related.length === 0 && !overview) return null;

  const select = (key: string) => { setActive(key); setPage(1); };

  // Inside a garage's bookings, a "Verkstad" column repeats that same garage on
  // every row. Drop whichever column links back to the parent we are already on.
  const columns = (activeResource?.columns || []).filter((c) => c.link !== parentKey);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1 border-b border-ink-800 pb-2">
        {overview && (
          <button
            onClick={() => select(OVERVIEW)}
            className={`rounded-md px-2.5 py-1.5 text-sm transition-colors ${
              active === OVERVIEW ? "bg-ink-800 text-white" : "text-ink-400 hover:bg-ink-850 hover:text-ink-200"
            }`}
          >
            Översikt
          </button>
        )}
        {related.map((tab) => (
          <button
            key={tab.key}
            onClick={() => select(tab.key)}
            className={`rounded-md px-2.5 py-1.5 text-sm transition-colors ${
              active === tab.key ? "bg-ink-800 text-white" : "text-ink-400 hover:bg-ink-850 hover:text-ink-200"
            }`}
          >
            {tab.label}
            {active === tab.key && data && (
              <span className="ml-1.5 num text-xs text-ink-500">{number(data.meta.total)}</span>
            )}
          </button>
        ))}
      </div>

      {active === OVERVIEW && overview}

      {activeResource && (
        <>
          <ResourceTable
            resourceKey={activeResource.key}
            columns={columns}
            rows={data?.rows || []}
            isFetching={isFetching}
            emptyMessage={`Inga ${activeResource.label.toLowerCase()} kopplade hit.`}
          />

          <div className="flex items-center justify-between text-xs">
            <Link
              to={`/r/${activeResource.key}`}
              className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-300"
            >
              Öppna hela {activeResource.label.toLowerCase()} <ExternalLink className="h-3 w-3" />
            </Link>

            {data && data.meta.pages > 1 && (
              <div className="flex items-center gap-2">
                <span className="num text-ink-500">
                  Sida {data.meta.page} av {number(data.meta.pages)}
                </span>
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-ghost py-1">
                  Föregående
                </button>
                <button
                  disabled={page >= data.meta.pages}
                  onClick={() => setPage(page + 1)}
                  className="btn-ghost py-1"
                >
                  Nästa
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
